// <AddEmployeeModal> — Org View Step 7 (T-053, plan §5 / spec §8).
//
// Centered modal launched by the header "+ Add Employee" CTA. Submits
// to the existing `POST /api/hires` endpoint, creating an OnboardingHire
// in PRE_BOARDING status. Per spec §8 the new hire does NOT immediately
// appear in the Org grid — they're in the onboarding flow until that
// completes. After a successful submit, the modal closes and we
// navigate to /onboarding so the HR user can pick up the flow.
//
// Field set comes from the screenshot reference in spec §8 + the
// underlying `OnboardingHire` model. The dedicated add-hire-modal-spec
// (inventory #15) doesn't exist yet — fields on the modal mirror the
// API's required+optional set today (name, role, startDate, stage),
// with onboarding status defaulting to PRE_BOARDING. The modal can
// be widened (email, department, location, visa-required) when those
// columns join either OnboardingHire or the auto-linked Employee row.

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { api, ApiError } from '../../api/client';

export interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

interface FormState {
  name: string;
  role: string;
  startDate: string;
  stage: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  role: '',
  startDate: '',
  stage: '',
};

const STAGE_OPTIONS = [
  'Pre-onboarding',
  'First week',
  'First month',
  'Other',
];

export function AddEmployeeModal({
  open,
  onClose,
  onAdded,
}: AddEmployeeModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; role?: string; submit?: string }>(
    {},
  );
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const formId = useId();

  // Reset state every time the modal closes.
  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  // Focus the first input on open.
  useEffect(() => {
    if (open && firstFieldRef.current) {
      const t = setTimeout(() => firstFieldRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc closes (parent owns the open state).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as 'name' | 'role']) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!form.name.trim()) nextErrors.name = 'Required';
    if (!form.role.trim()) nextErrors.role = 'Required';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/hires', {
        name: form.name.trim(),
        role: form.role.trim(),
        status: 'PRE_BOARDING',
        stage: form.stage || undefined,
        startDate: form.startDate || undefined,
      });
      onAdded();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : 'Could not create the hire. Please try again.';
      setErrors({ submit: message });
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
      data-testid="add-employee-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close add employee modal"
        onClick={onClose}
        data-testid="add-employee-modal-backdrop"
        className="absolute inset-0 bg-slate-900/40"
      />
      {/* Card */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <header className="px-6 py-5 flex items-center justify-between border-b border-slate-100">
          <h3
            id={`${formId}-title`}
            className="text-lg font-bold text-slate-800"
          >
            Add new hire
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="add-employee-modal-close"
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
          >
            <X size={18} aria-hidden />
          </button>
        </header>
        <form onSubmit={onSubmit} className="p-6 flex flex-col gap-5">
          <Field
            label="Full name"
            error={errors.name}
            id={`${formId}-name`}
            required
          >
            <input
              ref={firstFieldRef}
              id={`${formId}-name`}
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              data-testid="add-employee-modal-name"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6344C6]/20"
              autoComplete="off"
            />
          </Field>

          <Field
            label="Role / Title"
            error={errors.role}
            id={`${formId}-role`}
            required
          >
            <input
              id={`${formId}-role`}
              type="text"
              value={form.role}
              onChange={(e) => update('role', e.target.value)}
              data-testid="add-employee-modal-role"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6344C6]/20"
              autoComplete="off"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Start date" id={`${formId}-startDate`}>
              <input
                id={`${formId}-startDate`}
                type="date"
                value={form.startDate}
                onChange={(e) => update('startDate', e.target.value)}
                data-testid="add-employee-modal-startDate"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6344C6]/20"
              />
            </Field>
            <Field label="Onboarding stage" id={`${formId}-stage`}>
              <select
                id={`${formId}-stage`}
                value={form.stage}
                onChange={(e) => update('stage', e.target.value)}
                data-testid="add-employee-modal-stage"
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6344C6]/20"
              >
                <option value="">—</option>
                {STAGE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {errors.submit ? (
            <p
              role="alert"
              data-testid="add-employee-modal-error"
              className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2"
            >
              {errors.submit}
            </p>
          ) : null}

          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="add-employee-modal-cancel"
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="add-employee-modal-submit"
              className="bg-[#6344C6] disabled:bg-slate-300 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#553ABE] active:scale-95 shadow-md hover:shadow-lg transition"
            >
              {submitting ? 'Adding…' : 'Add hire'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function Field({
  label,
  error,
  id,
  required = false,
  children,
}: {
  label: string;
  error?: string;
  id: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold text-slate-600 mb-1.5"
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>
      {children}
      {error ? (
        <p
          className="text-xs text-rose-700 mt-1.5"
          data-testid={`${id}-error`}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
